import type { Catalog, CatalogDimension, CatalogMetric, PackCatalog } from "../../lib/api";
import { draftPack, metricRefsOf, usesFields, type Draft } from "./store";

/** Why a field can't be added: `group` heads the list it's collapsed into, `detail` is its tooltip. */
export interface Blocker { group: string; detail: string }

export interface FieldRow<F> {
  pack: PackCatalog;
  field: F;
  /** The field's label, plus its model's when the pack has another field by that name. */
  label: string;
  selected: boolean;
  blocker: Blocker | null;
}

/** Fields that can be added, by pack then model, and the rest by why they can't. */
export interface FieldSection<F> {
  available: { pack: PackCatalog; models: [string, FieldRow<F>[]][] }[];
  blocked: [string, FieldRow<F>[]][];
}

type Field = { ref: string; label: string; modelLabel: string; sameAs?: string; shared?: boolean };

/** Dimensions several models line up on (like Business day) are listed together, not under one model. */
const SHARED = "Shared";
const groupOf = (f: Field) => (f.shared ? SHARED : f.modelLabel);
const sharedFirst = <T>(groups: [string, T[]][]) => [...groups].sort(([a], [b]) => Number(b === SHARED) - Number(a === SHARED));

/**
 * Labels for a pack's fields of one kind. A label that more than one field has gets its model's
 * label added ("Amount · Ledger"), so look-alike fields can be told apart wherever they're listed.
 * A dimension declared the same as another (`sameAs`) always gets it, and doesn't count against the
 * shared one, which is the one listed.
 */
export function displayLabels(fields: Field[]): Map<string, string> {
  const count = new Map<string, number>();
  for (const f of fields) if (!f.sameAs) count.set(f.label, (count.get(f.label) ?? 0) + 1);
  return new Map(fields.map((f) => [f.ref, f.sameAs || count.get(f.label)! > 1 ? `${f.label} · ${f.modelLabel}` : f.label]));
}

function groupBy<T>(xs: T[], key: (x: T) => string): [string, T[]][] {
  const m = new Map<string, T[]>();
  for (const x of xs) {
    const k = key(x);
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(x);
  }
  return [...m.entries()];
}

function section<F extends Field>(packs: PackCatalog[], rows: FieldRow<F>[]): FieldSection<F> {
  const open = rows.filter((r) => !r.blocker);
  return {
    available: packs
      .map((pack) => ({ pack, models: sharedFirst(groupBy(open.filter((r) => r.pack === pack), (r) => groupOf(r.field))) }))
      .filter((p) => p.models.length),
    blocked: groupBy(rows.filter((r) => r.blocker), (r) => r.blocker!.group),
  };
}

/**
 * The picker's metrics and groupings for a draft, narrowed by a search. A field is blocked when it's
 * from a pack other than the one the widget reads, or when it can't be combined with what's already
 * chosen without double-counting. Chosen fields are never blocked.
 */
export function fieldList(packs: PackCatalog[], draft: Draft, q = "") {
  const active = draftPack(draft, packs);
  const locked = usesFields(draft);
  const several = packs.length > 1;
  const metricRefs = metricRefsOf(draft.metrics);
  const dimRefs = [...draft.dimensions.map((d) => d.ref), ...draft.filters.map((f) => f.ref).filter(Boolean)];
  const metricLabel = new Map(active.metrics.map((m) => [m.ref, m.label]));
  const dimByRef = new Map(active.dimensions.map((d) => [d.ref, d]));

  const otherPack = (pack: PackCatalog): Blocker | null =>
    locked && pack.name !== active.name
      ? {
          group: `${pack.label} data`,
          detail: `This widget uses ${active.label} data, and a widget reads one data source. Remove its fields to use ${pack.label}.`,
        }
      : null;
  const metricBlocker = (pack: PackCatalog, m: CatalogMetric): Blocker | null => {
    const other = otherPack(pack);
    if (other) return other;
    const bad = dimRefs.find((d) => !pack.compatibility[m.ref]?.includes(d));
    if (!bad) return null;
    const dim = dimByRef.get(bad);
    return {
      group: `Would double-count with ${dim?.label ?? bad}`,
      detail: `Can't combine with the "${dim?.label ?? bad}" grouping or filter — ${m.modelLabel.toLowerCase()} aren't linked to exactly one ${(dim?.shared ? dim.label : dim?.modelLabel)?.toLowerCase() ?? "record"}, so it would double-count.`,
    };
  };
  const dimBlocker = (pack: PackCatalog, d: CatalogDimension): Blocker | null => {
    const other = otherPack(pack);
    if (other) return other;
    const bad = metricRefs.find((m) => !pack.compatibility[m]?.includes(d.ref));
    if (!bad) return null;
    const metric = metricLabel.get(bad) ?? bad;
    return { group: `Can't split ${metric}`, detail: `"${metric}" can't be split by ${d.label.toLowerCase()} — it would double-count or misattribute.` };
  };

  const isActive = (pack: PackCatalog) => pack.name === active.name;
  const metricRows = packs.flatMap((pack) => {
    const labels = displayLabels(pack.metrics);
    return pack.metrics.map((m): FieldRow<CatalogMetric> => {
      const selected = isActive(pack) && draft.metrics.some((x) => x.kind === "metric" && x.ref === m.ref);
      return { pack, field: m, label: labels.get(m.ref)!, selected, blocker: selected ? null : metricBlocker(pack, m) };
    });
  });
  const dimRows = packs.flatMap((pack) => {
    const labels = displayLabels(pack.dimensions);
    return pack.dimensions.flatMap((d): FieldRow<CatalogDimension>[] => {
      const selected = isActive(pack) && draft.dimensions.some((x) => x.ref === d.ref);
      // A look-alike shows only where a saved widget already uses it; the shared one is offered instead.
      if (d.sameAs && !selected) return [];
      return [{ pack, field: d, label: labels.get(d.ref)!, selected, blocker: selected ? null : dimBlocker(pack, d) }];
    });
  });

  const needle = q.toLowerCase();
  const match = (s?: string) => !!s && s.toLowerCase().includes(needle);
  const matches = (r: FieldRow<Field & { description?: string }>) =>
    match(r.label) || match(groupOf(r.field)) || match(r.field.modelLabel) || match(r.field.description) || (several && match(r.pack.label));

  return {
    active,
    metrics: section(packs, metricRows.filter(matches)),
    dimensions: section(packs, dimRows.filter(matches)),
  };
}

/**
 * What the Filters panel offers: non-time fields every chosen metric can be split by, grouped by
 * model (shared ones first, under Shared), with look-alike labels told apart.
 */
export function filterFields(catalog: Catalog, draft: Draft) {
  const refs = metricRefsOf(draft.metrics);
  const fields = catalog.dimensions.filter((d) => d.type !== "time" && !d.sameAs);
  const allowed = fields.filter((d) => refs.every((r) => catalog.compatibility[r]?.includes(d.ref)));
  return {
    allowed,
    byModel: sharedFirst(groupBy(allowed, groupOf)),
    labels: displayLabels(catalog.dimensions),
  };
}
